import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronRightIcon, MapPinIcon, TrendingUpIcon, AwardIcon } from 'lucide-react';
import Button from '../components/ui/Button';
import Input from '../components/ui/Input';
import { useOrder } from '../context/OrderContext';
import { useAchievement } from '../context/AchievementContext';
import AnimatedCounter from '../components/ui/AnimatedCounter';
const Home: React.FC = () => {
  const [postalCode, setPostalCode] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const {
    setDeliveryInfo
  } = useOrder();
  const {
    points,
    level
  } = useAchievement();
  const [showPoints, setShowPoints] = useState(false);
  useEffect(() => {
    // Show points animation after a short delay
    const timer = setTimeout(() => {
      setShowPoints(true);
    }, 500);
    return () => clearTimeout(timer);
  }, []);
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    // Set initial delivery info with postal code
    setDeliveryInfo({
      postalCode,
      address: '',
      date: '',
      timeSlot: '',
      mealType: ''
    });
    // Simulate loading for better UX
    setTimeout(() => {
      // Navigate to service availability screen
      navigate('/service-availability');
    }, 600);
  };
  const handleCategoryClick = (category: string) => {
    navigate('/food', {
      state: {
        category
      }
    });
  };
  return <div className="min-h-screen bg-gray-100 -mt-6 md:-mt-0 pb-20 md:pb-0" data-id="element-671">
      {/* Hero section */}
      <div className="bg-[#E84118] text-white py-12 md:py-16 px-4 relative overflow-hidden" data-id="element-672">
        <div className="absolute inset-0 overflow-hidden" data-id="element-673">
          <div className="absolute right-0 top-0 w-96 h-96 bg-white opacity-10 rounded-full transform translate-x-1/2 -translate-y-1/2" data-id="element-674"></div>
          <div className="absolute left-0 bottom-0 w-64 h-64 bg-white opacity-10 rounded-full transform -translate-x-1/2 translate-y-1/2" data-id="element-675"></div>
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative" data-id="element-676">
          <div className="text-center" data-id="element-677">
            <h1 className="text-3xl font-bold sm:text-4xl lg:text-5xl mb-4" data-id="element-678">
              Delicious Food Delivered To Your Doorstep
            </h1>
            <p className="mt-3 max-w-md mx-auto text-xl opacity-90" data-id="element-679">
              Order your favorite meals with just a few clicks
            </p>
            {showPoints && <div className="mt-6 inline-flex items-center bg-white bg-opacity-20 rounded-lg px-4 py-2 text-sm md:text-base animate-fadeIn cursor-pointer" onClick={() => navigate('/food-passport')} data-id="element-680">
                <AwardIcon size={18} className="mr-2" data-id="element-681" />
                <span data-id="element-682">
                  You have{' '}
                  <AnimatedCounter value={points} className="font-bold" data-id="element-683" />{' '}
                  points | Level {level}
                </span>
              </div>}
          </div>
        </div>
      </div>
      {/* Postal code section */}
      <div className="max-w-3xl mx-auto -mt-8 px-4 sm:px-6" data-id="element-684">
        <div className="bg-white rounded-lg shadow-lg p-6 transform transition-all duration-300 hover:shadow-xl" data-id="element-685">
          <h2 className="text-xl font-semibold mb-4" data-id="element-686">
            Enter your postal code to get started
          </h2>
          <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-4" data-id="element-687">
            <div className="flex-1" data-id="element-688">
              <div className="relative" data-id="element-689">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-690">
                  <MapPinIcon size={18} className="text-gray-400" data-id="element-691" />
                </div>
                <input type="text" placeholder="Enter your postal code" value={postalCode} onChange={e => setPostalCode(e.target.value)} className="pl-10 w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-[#E84118] focus:border-[#E84118] transition-all duration-200" required data-id="element-692" />
              </div>
            </div>
            <Button type="submit" disabled={isLoading || !postalCode.trim()} className="transform hover:scale-105 transition-transform duration-200" data-id="element-693">
              <span className="flex items-center" data-id="element-694">
                {isLoading ? <>
                    <span className="mr-2 animate-spin" data-id="element-695">⏳</span> Finding...
                  </> : <>
                    Find Food <ChevronRightIcon size={18} className="ml-1" data-id="element-696" />
                  </>}
              </span>
            </Button>
          </form>
        </div>
      </div>
      {/* Featured categories */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-id="element-697">
        <h2 className="text-2xl font-bold mb-6" data-id="element-698">Popular Categories</h2>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4" data-id="element-699">
          {['Breakfast', 'Lunch', 'Dinner', 'Healthy', 'Vegetarian', 'Dessert'].map((category, index) => <div key={category} className="bg-white rounded-lg shadow-md p-4 text-center hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1 cursor-pointer" style={{
          animationDelay: `${index * 0.1}s`
        }} onClick={() => handleCategoryClick(category.toLowerCase())} role="button" aria-label={`Browse ${category} category`} data-id="element-700">
              <div className="w-16 h-16 bg-gray-200 rounded-full mx-auto mb-3 flex items-center justify-center" data-id="element-701">
                <span className="text-xl" data-id="element-702">
                  {category === 'Breakfast' && '🍳'}
                  {category === 'Lunch' && '🍱'}
                  {category === 'Dinner' && '🍽️'}
                  {category === 'Healthy' && '🥗'}
                  {category === 'Vegetarian' && '🥦'}
                  {category === 'Dessert' && '🍰'}
                </span>
              </div>
              <h3 className="font-medium" data-id="element-703">{category}</h3>
            </div>)}
        </div>
      </div>
      {/* Banner */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6" data-id="element-704">
        <div className="bg-yellow-50 border border-yellow-100 rounded-lg p-6 transform hover:scale-[1.01] transition-transform duration-300" data-id="element-705">
          <div className="flex flex-col sm:flex-row items-center" data-id="element-706">
            <div className="flex-1" data-id="element-707">
              <h3 className="text-xl font-bold text-yellow-800" data-id="element-708">
                Group ordering made easy
              </h3>
              <p className="text-yellow-700 mt-1" data-id="element-709">
                Order together with friends, family or colleagues
              </p>
            </div>
            <div className="mt-4 sm:mt-0" data-id="element-710">
              <Button onClick={() => navigate('/group-order/create')} className="transform hover:scale-105 transition-transform duration-200" data-id="element-711">
                Start Group Order
              </Button>
            </div>
          </div>
        </div>
      </div>
      {/* Recent orders */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6" data-id="element-712">
        <div className="flex items-center justify-between mb-6" data-id="element-713">
          <h2 className="text-2xl font-bold" data-id="element-714">Your Recent Orders</h2>
          <div className="flex items-center text-sm text-[#E84118]" data-id="element-715">
            <TrendingUpIcon size={16} className="mr-1" data-id="element-716" />
            <span data-id="element-717">Earn 50 points per order</span>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-md p-6 text-center transform hover:shadow-lg transition-all duration-300" data-id="element-718">
          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-719">
            <span className="text-2xl" data-id="element-720">🍽️</span>
          </div>
          <p className="text-gray-500 mb-4" data-id="element-721">You don't have any recent orders</p>
          <Button variant="outline" className="mt-2 transform hover:scale-105 transition-transform duration-200" onClick={() => navigate('/food')} data-id="element-722">
            Browse Menu
          </Button>
        </div>
      </div>
      {/* CSS for animations */}
      <style jsx data-id="element-723">{`
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-fadeIn {
          animation: fadeIn 0.5s ease-out forwards;
        }
      `}</style>
    </div>;
};
export default Home;